import * as m from "~/paraglide/messages.js";

/**
 * The questions people actually ask before starting, grouped by what they are
 * really worried about: whether this is another tool, what exactly gets built,
 * whether they keep control of their own systems, and what working together
 * costs them.
 *
 * `FaqItem` stays at question-and-answer because `createFaqPageSchema` and the
 * blog's extracted FAQs both consume it. `FaqEntry` adds what only this page
 * needs: an anchor, an open-by-default flag, and a hand-off link that is
 * rendered after the answer and deliberately kept out of the schema.
 */

/**
 * The minimum a question needs to reach the FAQPage schema. The blog's own
 * extractor builds these from markdown headings, so it stays this small.
 */
export interface FaqItem {
  answer: string;
  question: string;
}

/** A question as the FAQ page renders it. */
export interface FaqEntry extends FaqItem {
  /** Open on load. The design opens the first question and no other. */
  defaultOpen?: boolean;
  /** Anchor id, so an answer can be linked to directly. */
  id: string;
  /** A hand-off at the end of the answer. Never reaches the schema. */
  link?: { hash?: string; label: string; to: string };
}

export interface FaqCategory {
  category: string;
  /** Stable, locale-independent anchor id (e.g. `/faq#getting-started`). */
  id: string;
  items: FaqEntry[];
}

export function getFaqCategories(): FaqCategory[] {
  return [
    {
      category: m.faq_group_getting_started(),
      id: "getting-started",
      items: [
        {
          answer: m.faq_different_answer(),
          defaultOpen: true,
          id: "different",
          link: {
            label: m.faq_different_link(),
            to: "/services",
          },
          question: m.faq_different_question(),
        },
        {
          answer: m.faq_first_task_answer(),
          id: "first-task",
          question: m.faq_first_task_question(),
        },
        {
          answer: m.faq_cost_answer(),
          id: "cost",
          question: m.faq_cost_question(),
        },
      ],
    },
    {
      // The questions buyers put to search engines and AI assistants before
      // they ever reach the site: does this studio build agents, RAG, n8n,
      // integrations, and does it work where I am.
      category: m.faq_group_what_we_build(),
      id: "what-we-build",
      items: [
        {
          answer: m.faq_agents_answer(),
          id: "ai-agents",
          link: {
            hash: "agent-teams",
            label: m.faq_different_link(),
            to: "/services",
          },
          question: m.faq_agents_question(),
        },
        {
          answer: m.faq_rag_answer(),
          id: "rag",
          link: {
            hash: "save-time",
            label: m.faq_different_link(),
            to: "/services",
          },
          question: m.faq_rag_question(),
        },
        {
          answer: m.faq_n8n_answer(),
          id: "n8n",
          question: m.faq_n8n_question(),
        },
        {
          answer: m.faq_integration_answer(),
          id: "integration",
          question: m.faq_integration_question(),
        },
        {
          answer: m.faq_processes_answer(),
          id: "processes",
          link: { label: m.faq_processes_link(), to: "/pain-points" },
          question: m.faq_processes_question(),
        },
        {
          answer: m.faq_where_answer(),
          id: "where",
          question: m.faq_where_question(),
        },
      ],
    },
    {
      category: m.faq_group_tools_and_control(),
      id: "tools-and-control",
      items: [
        {
          answer: m.faq_replace_answer(),
          id: "replace",
          question: m.faq_replace_question(),
        },
        {
          answer: m.faq_wrong_answer(),
          id: "wrong",
          question: m.faq_wrong_question(),
        },
        {
          answer: m.faq_approve_answer(),
          id: "approve",
          question: m.faq_approve_question(),
        },
        {
          answer: m.faq_data_answer(),
          id: "data",
          question: m.faq_data_question(),
        },
      ],
    },
    {
      category: m.faq_group_working_together(),
      id: "working-together",
      items: [
        {
          answer: m.faq_involvement_answer(),
          id: "involvement",
          question: m.faq_involvement_question(),
        },
        {
          answer: m.faq_handover_answer(),
          id: "handover",
          link: {
            hash: "launch",
            label: m.faq_handover_link(),
            to: "/how-we-work",
          },
          question: m.faq_handover_question(),
        },
        {
          answer: m.faq_upkeep_answer(),
          id: "upkeep",
          question: m.faq_upkeep_question(),
        },
      ],
    },
  ];
}

/** Every question, flattened to the shape the FAQPage schema consumes. */
export function getFaqItems(): FaqItem[] {
  return getFaqCategories().flatMap((category) =>
    category.items.map(({ answer, question }) => ({ answer, question }))
  );
}
